import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MenuItem, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/portalHelpers";
import {
  PageHeader,
  Panel,
  StatusChip,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
  statusTone,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerTasks, updatePartnerTaskStatus } from "../../api/endpoints/tasks";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { STATUSES, STATUS_LABELS } from "./taskHelpers";

const PAGE_SIZE = 20;

/**
 * A partner is an ordinary task assignee: they see what they were handed and may move its status.
 * There is deliberately no way to edit the work itself — that tier belongs to whoever set it up.
 */
const TasksPage = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => ({ page, size: PAGE_SIZE, ...(status !== "all" ? { status } : {}) }),
    [page, status]
  );

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.tasks(params),
    () => fetchPartnerTasks(params),
    { keepPreviousData: true }
  );

  const move = useMutation(updatePartnerTaskStatus, {
    onSuccess: () => {
      toast.success("Status updated");
      queryClient.invalidateQueries(partnerQueryKeys.tasks(params));
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "That status change was refused."),
  });

  const rows = data?.content || [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div>
      <PageHeader title="Tasks" />

      <div className="mb-4 max-w-xs">
        <TextField
          fullWidth
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {STATUS_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {isLoading ? (
        <LoadingPanel label="Loading your tasks…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={status === "all" ? "Nothing assigned to you" : "Nothing with that status"}
          description="Tasks the office hands you will appear here."
        />
      ) : (
        <Panel title={`${data?.totalItems ?? rows.length} task${rows.length === 1 ? "" : "s"}`}>
          <ul className="list-none p-0 m-0">
            {rows.map((task) => (
              <li
                key={task.id}
                className="flex flex-wrap items-start justify-between gap-3 py-3 border-b border-slate-100 last:border-0"
              >
                <div className="min-w-0">
                  <Link
                    to={`/partner/tasks/${task.id}`}
                    className="text-sm font-semibold text-slate-900 no-underline hover:text-teal-700"
                  >
                    {task.title || "Untitled task"}
                  </Link>
                  <p className="text-xs text-slate-500 m-0 mt-0.5">
                    {task.assignedByName ? `From ${task.assignedByName}` : "From the office"}
                    {task.dueDate ? ` · due ${formatDate(task.dueDate)}` : ""}
                    {task.priority ? ` · ${task.priority}` : ""}
                  </p>
                  {task.clientId ? (
                    <Link
                      to={`/partner/clients/${task.clientId}`}
                      className="text-xs font-medium text-teal-700 no-underline"
                    >
                      {task.clientName || "View client"}
                    </Link>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  <StatusChip tone={statusTone(task.status)}>
                    {STATUS_LABELS[task.status] || task.status || "—"}
                  </StatusChip>
                  <TextField
                    select
                    size="small"
                    value={task.status || ""}
                    disabled={move.isLoading}
                    onChange={(event) =>
                      move.mutate({ taskId: task.id, status: event.target.value })
                    }
                    sx={{ minWidth: 150 }}
                  >
                    {STATUSES.map((value) => (
                      <MenuItem key={value} value={value}>
                        {STATUS_LABELS[value]}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </li>
            ))}
          </ul>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </Panel>
      )}
    </div>
  );
};

export default TasksPage;
